import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { Ban, CheckCircle2, Copy, Eye, EyeOff, KeyRound, MessageCircle, Users, Wallet } from "lucide-react";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import {
  CompactStatCard,
  DataTable,
  PageHeader,
  StatsGrid,
  type DataTableColumn,
} from "@/components/shared";
import {
  AdminAdjustWalletDialog,
  type AdminAdjustWalletTarget,
} from "@/components/admin/AdminAdjustWalletDialog";
import { Skeleton } from "@/components/ui/skeleton";
import { useToast } from "@/hooks/use-toast";
import { useAdminActivateMemberMutation, useAdminUsers, useUpdateUserStatusMutation } from "@/hooks/queries";
import { getApiErrorMessage } from "@/services/httpClient";
import type { AdminUserListDto } from "@/types/admin-user.dto";

function displayName(user: AdminUserListDto) {
  return [user.firstName, user.lastName].filter(Boolean).join(" ") || user.userName || user.email;
}

/** Normalize phone to WhatsApp international digits (default India +91). */
function toWhatsAppPhone(phone?: string | null): string | null {
  if (!phone?.trim()) return null;
  const digits = phone.replace(/\D/g, "");
  if (digits.length === 10) return `91${digits}`;
  if (digits.length >= 11) return digits.replace(/^0+/, "");
  return null;
}

function buildLoginCredentialsMessage(user: AdminUserListDto): string {
  const loginUrl = `${window.location.origin}/auth/login`;
  const name = displayName(user);
  const username = user.userName || user.email;
  const password = user.plainPassword?.trim() || "(not stored — ask user to reset)";
  return [
    `Hello ${name},`,
    "",
    "Your Hyperlocal login details:",
    `Username: ${username}`,
    `Password: ${password}`,
    `Login: ${loginUrl}`,
    "",
    "Please keep this private and change your password after login.",
  ].join("\n");
}

function openWhatsAppCredentials(user: AdminUserListDto): boolean {
  const phone = toWhatsAppPhone(user.phoneNumber);
  if (!phone) return false;
  const text = encodeURIComponent(buildLoginCredentialsMessage(user));
  window.open(`https://wa.me/${phone}?text=${text}`, "_blank", "noopener,noreferrer");
  return true;
}

function DetailRow({
  label,
  value,
  mono,
  onCopy,
}: {
  label: string;
  value?: string | null;
  mono?: boolean;
  onCopy?: (text: string) => void;
}) {
  const text = value?.trim() ? value : "—";
  const canCopy = Boolean(value?.trim() && onCopy);
  return (
    <div className="flex items-start justify-between gap-3 border-b border-border/60 py-2.5 last:border-0">
      <div className="min-w-0">
        <p className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</p>
        <p className={`mt-0.5 break-all text-sm text-foreground ${mono ? "font-mono" : ""}`}>{text}</p>
      </div>
      {canCopy ? (
        <Button
          type="button"
          size="icon"
          variant="ghost"
          className="h-7 w-7 shrink-0"
          onClick={() => onCopy?.(value!.trim())}
          aria-label={`Copy ${label}`}
        >
          <Copy className="h-3.5 w-3.5" />
        </Button>
      ) : null}
    </div>
  );
}

const AdminUsers = () => {
  const { toast } = useToast();
  const [search, setSearch] = useState("");
  const [role, setRole] = useState<string>("all");
  const [activeFilter, setActiveFilter] = useState<string>("all");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [adjustTarget, setAdjustTarget] = useState<AdminAdjustWalletTarget | null>(null);
  const [selectedUser, setSelectedUser] = useState<AdminUserListDto | null>(null);
  const [revealedPasswords, setRevealedPasswords] = useState<Record<string, boolean>>({});

  const togglePasswordReveal = (userId: string) => {
    setRevealedPasswords((prev) => ({ ...prev, [userId]: !prev[userId] }));
  };

  const shareWhatsApp = (user: AdminUserListDto) => {
    if (!openWhatsAppCredentials(user)) {
      toast({
        title: "Phone number required",
        description: "Add a valid mobile number to send WhatsApp credentials.",
        variant: "destructive",
      });
      return;
    }
    toast({ title: "Opening WhatsApp", description: displayName(user) });
  };

  const filter = useMemo(
    () => ({
      search: search.trim() || undefined,
      role: role === "all" ? undefined : role,
      isActive: activeFilter === "all" ? undefined : activeFilter === "active",
      page,
      pageSize,
    }),
    [search, role, activeFilter, page, pageSize],
  );

  const { data, isLoading } = useAdminUsers(filter);
  const updateStatusMutation = useUpdateUserStatusMutation();
  const adminActivateMemberMutation = useAdminActivateMemberMutation();
  const users = data?.items ?? [];

  const copyText = (text: string, label: string) => {
    navigator.clipboard.writeText(text);
    toast({ title: `${label} copied` });
  };

  const toggleStatus = async (user: AdminUserListDto) => {
    try {
      await updateStatusMutation.mutateAsync({ userId: user.id, isActive: !user.isActive });
      toast({
        title: user.isActive ? "User deactivated" : "User activated",
        description: user.email,
      });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const activateMemberId = async (user: AdminUserListDto) => {
    try {
      await adminActivateMemberMutation.mutateAsync(user.id);
      toast({
        title: "Member ID activated",
        description: user.email,
      });
    } catch (error) {
      toast({ title: getApiErrorMessage(error), variant: "destructive" });
    }
  };

  const openAdjust = (user: AdminUserListDto) => {
    setAdjustTarget({
      userId: user.id,
      name: displayName(user),
      email: user.email,
      memberCode: user.memberCode,
    });
  };

  const columns: DataTableColumn<AdminUserListDto>[] = [
    {
      id: "user",
      header: "User",
      cell: (row) => (
        <div className="min-w-[160px]">
          <p className="text-sm font-medium">{displayName(row)}</p>
          <p className="text-xs text-muted-foreground">{row.email}</p>
          {row.userName ? (
            <p className="mt-0.5 font-mono text-[11px] text-muted-foreground">@{row.userName}</p>
          ) : null}
        </div>
      ),
    },
    {
      id: "username",
      header: "Username",
      cell: (row) =>
        row.userName ? (
          <button
            type="button"
            className="font-mono text-xs hover:underline"
            onClick={() => copyText(row.userName!, "Username")}
          >
            {row.userName}
          </button>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "member",
      header: "Member ID",
      cell: (row) => (
        <div className="space-y-1 text-xs">
          {row.memberCode ? (
            <button
              type="button"
              className="font-mono hover:underline"
              onClick={() => copyText(row.memberCode!, "Member ID")}
            >
              {row.memberCode}
            </button>
          ) : (
            <span className="text-muted-foreground">—</span>
          )}
          <div className="flex flex-wrap gap-1">
            {row.isMemberActive && (
              <Badge className="text-[10px]" variant="outline">
                Active
              </Badge>
            )}
            {row.isPrimeMember && (
              <Badge className="text-[10px]" variant="secondary">
                Prime
              </Badge>
            )}
          </div>
        </div>
      ),
    },
    {
      id: "referral",
      header: "Referral code",
      cell: (row) =>
        row.referralCode ? (
          <button
            type="button"
            className="font-mono text-xs hover:underline"
            onClick={() => copyText(row.referralCode!, "Referral code")}
          >
            {row.referralCode}
          </button>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "referredBy",
      header: "Referred by",
      cell: (row) =>
        row.referredByReferralCode || row.referredByEmail ? (
          <div className="text-xs">
            {row.referredByReferralCode ? (
              <p className="font-mono">{row.referredByReferralCode}</p>
            ) : null}
            <p className="text-muted-foreground">{row.referredByEmail ?? row.referredByUserName}</p>
          </div>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      id: "phone",
      header: "Phone",
      cell: (row) => row.phoneNumber ?? "—",
    },
    {
      id: "password",
      header: "Password",
      cell: (row) => {
        const pwd = row.plainPassword?.trim();
        if (!pwd) return <span className="text-muted-foreground">—</span>;
        const shown = revealedPasswords[row.id];
        return (
          <div className="flex items-center gap-1">
            <button
              type="button"
              className="font-mono text-xs hover:underline"
              onClick={() => copyText(pwd, "Password")}
              title="Copy password"
            >
              {shown ? pwd : "••••••••"}
            </button>
            <Button
              type="button"
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              onClick={() => togglePasswordReveal(row.id)}
              aria-label={shown ? "Hide password" : "Show password"}
            >
              {shown ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            </Button>
          </div>
        );
      },
    },
    {
      id: "roles",
      header: "Role",
      cell: (row) => {
        const labels = row.roles.length
          ? row.roles
          : [row.appRole === "Store" ? "Store Owner" : row.appRole || "Customer"];
        return (
          <div className="space-y-1">
            <div className="flex flex-wrap gap-1">
              {labels.map((r) => (
                <Badge
                  key={r}
                  variant={r === "Store Owner" || r === "Store" ? "default" : "secondary"}
                  className="text-xs"
                >
                  {r}
                </Badge>
              ))}
            </div>
            {row.hasStore && row.storeName ? (
              <p className="max-w-[160px] truncate text-[11px] text-muted-foreground" title={row.storeName}>
                {row.storeName}
              </p>
            ) : null}
          </div>
        );
      },
    },
    {
      id: "status",
      header: "Status",
      cell: (row) => (
        <Badge variant={row.isActive ? "default" : "destructive"} className="text-xs">
          {row.isActive ? "Active" : "Inactive"}
        </Badge>
      ),
    },
    {
      id: "actions",
      header: "",
      cell: (row) => (
        <div className="flex flex-wrap justify-end gap-2">
          <Button size="sm" variant="ghost" className="h-8 text-xs" onClick={() => setSelectedUser(row)}>
            <Eye className="mr-1 h-3.5 w-3.5" /> Details
          </Button>
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={!toWhatsAppPhone(row.phoneNumber)}
            onClick={() => shareWhatsApp(row)}
          >
            <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp
          </Button>
          <Button size="sm" variant="outline" className="h-8 text-xs" onClick={() => openAdjust(row)}>
            <Wallet className="mr-1 h-3.5 w-3.5" /> Adjust
          </Button>
          <Button size="sm" variant="ghost" className="h-8 text-xs" asChild>
            <Link to={`/admin/wallet/users/${row.id}`}>Ledger</Link>
          </Button>
          {!row.isMemberActive && (
            <Button
              size="sm"
              variant="secondary"
              className="h-8 text-xs"
              disabled={adminActivateMemberMutation.isPending}
              onClick={() => activateMemberId(row)}
            >
              <KeyRound className="mr-1 h-3.5 w-3.5" /> Activate Member
            </Button>
          )}
          <Button
            size="sm"
            variant="outline"
            className="h-8 text-xs"
            disabled={updateStatusMutation.isPending}
            onClick={() => toggleStatus(row)}
          >
            {row.isActive ? (
              <>
                <Ban className="mr-1 h-3.5 w-3.5" /> Deactivate
              </>
            ) : (
              <>
                <CheckCircle2 className="mr-1 h-3.5 w-3.5" /> Activate
              </>
            )}
          </Button>
        </div>
      ),
    },
  ];

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-10 w-48" />
        <Skeleton className="h-96 rounded-xl" />
      </div>
    );
  }

  const activeCount = users.filter((u) => u.isActive).length;

  return (
    <div className="space-y-6">
      <PageHeader
        title="User Management"
        description="Browse users with username, member ID, referral code, email, and sponsor details."
      />

      <StatsGrid>
        <CompactStatCard title="Total (page)" value={users.length} icon={Users} />
        <CompactStatCard title="Active (page)" value={activeCount} icon={CheckCircle2} />
        <CompactStatCard title="All users" value={data?.totalItems ?? 0} icon={Users} />
      </StatsGrid>

      <div className="flex flex-wrap gap-3">
        <Select
          value={role}
          onValueChange={(v) => {
            setRole(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[140px]">
            <SelectValue placeholder="Role" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All roles</SelectItem>
            <SelectItem value="Admin">Admin</SelectItem>
            <SelectItem value="Store">Store Owner</SelectItem>
            <SelectItem value="Delivery">Delivery</SelectItem>
            <SelectItem value="Customer">Customer</SelectItem>
          </SelectContent>
        </Select>
        <Select
          value={activeFilter}
          onValueChange={(v) => {
            setActiveFilter(v);
            setPage(1);
          }}
        >
          <SelectTrigger className="h-9 w-[140px]">
            <SelectValue placeholder="Status" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All status</SelectItem>
            <SelectItem value="active">Active</SelectItem>
            <SelectItem value="inactive">Inactive</SelectItem>
          </SelectContent>
        </Select>
      </div>

      <DataTable
        columns={columns}
        data={users}
        getRowId={(row) => row.id}
        searchPlaceholder="Search name, email, username, member ID, referral…"
        search={search}
        onSearchChange={(v) => {
          setSearch(v);
          setPage(1);
        }}
        emptyMessage="No users found"
        loading={isLoading}
        pagination={{
          page,
          pageSize: data?.pageSize ?? pageSize,
          totalCount: data?.totalItems ?? 0,
          totalPages: data?.totalPages ?? 1,
          from: data?.totalItems ? (page - 1) * (data?.pageSize ?? pageSize) + 1 : 0,
          to: Math.min(page * (data?.pageSize ?? pageSize), data?.totalItems ?? 0),
        }}
        onPageChange={setPage}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
      />

      <AdminAdjustWalletDialog
        open={Boolean(adjustTarget)}
        onOpenChange={(open) => !open && setAdjustTarget(null)}
        target={adjustTarget}
      />

      <Sheet open={Boolean(selectedUser)} onOpenChange={(open) => !open && setSelectedUser(null)}>
        <SheetContent className="w-full overflow-y-auto sm:max-w-md">
          {selectedUser ? (
            <>
              <SheetHeader>
                <SheetTitle>{displayName(selectedUser)}</SheetTitle>
                <SheetDescription>Account identifiers and referral details</SheetDescription>
              </SheetHeader>

              <div className="mt-6 space-y-1">
                <DetailRow label="Full name" value={displayName(selectedUser)} />
                <DetailRow
                  label="Username"
                  value={selectedUser.userName}
                  mono
                  onCopy={(v) => copyText(v, "Username")}
                />
                <DetailRow
                  label="Email"
                  value={selectedUser.email}
                  onCopy={(v) => copyText(v, "Email")}
                />
                <DetailRow
                  label="Phone"
                  value={selectedUser.phoneNumber}
                  onCopy={(v) => copyText(v, "Phone")}
                />
                <DetailRow
                  label="Password"
                  value={selectedUser.plainPassword}
                  mono
                  onCopy={(v) => copyText(v, "Password")}
                />
                <DetailRow
                  label="Member ID"
                  value={selectedUser.memberCode}
                  mono
                  onCopy={(v) => copyText(v, "Member ID")}
                />
                <DetailRow
                  label="Referral code"
                  value={selectedUser.referralCode}
                  mono
                  onCopy={(v) => copyText(v, "Referral code")}
                />
                <DetailRow
                  label="Referred by code"
                  value={selectedUser.referredByReferralCode}
                  mono
                  onCopy={(v) => copyText(v, "Sponsor referral code")}
                />
                <DetailRow label="Referred by username" value={selectedUser.referredByUserName} mono />
                <DetailRow label="Referred by email" value={selectedUser.referredByEmail} />
                <DetailRow
                  label="Role"
                  value={
                    selectedUser.roles.length
                      ? selectedUser.roles.join(", ")
                      : selectedUser.appRole === "Store"
                        ? "Store Owner"
                        : selectedUser.appRole || "Customer"
                  }
                />
                {selectedUser.hasStore && selectedUser.storeName ? (
                  <DetailRow label="Store" value={selectedUser.storeName} />
                ) : null}
                <DetailRow label="KYC" value={selectedUser.kycStatusText} />
                <DetailRow
                  label="Account status"
                  value={selectedUser.isActive ? "Active" : "Inactive"}
                />
                <DetailRow
                  label="Member status"
                  value={
                    [
                      selectedUser.isMemberActive ? "Member active" : "Member inactive",
                      selectedUser.isPrimeMember ? "Prime" : null,
                    ]
                      .filter(Boolean)
                      .join(" · ")
                  }
                />
                <DetailRow
                  label="Wallet"
                  value={
                    selectedUser.hasWallet
                      ? `₹${Number(selectedUser.availableBalance ?? 0).toFixed(2)} available`
                      : "No wallet"
                  }
                />
                <DetailRow
                  label="Joined"
                  value={new Date(selectedUser.createdAt).toLocaleString()}
                />
                <DetailRow label="User ID" value={selectedUser.id} mono onCopy={(v) => copyText(v, "User ID")} />
              </div>

              <div className="mt-6 flex flex-wrap gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={!toWhatsAppPhone(selectedUser.phoneNumber)}
                  onClick={() => shareWhatsApp(selectedUser)}
                >
                  <MessageCircle className="mr-1 h-3.5 w-3.5" /> WhatsApp credentials
                </Button>
                <Button size="sm" variant="outline" onClick={() => openAdjust(selectedUser)}>
                  <Wallet className="mr-1 h-3.5 w-3.5" /> Adjust wallet
                </Button>
                <Button size="sm" variant="ghost" asChild>
                  <Link to={`/admin/wallet/users/${selectedUser.id}`}>View ledger</Link>
                </Button>
              </div>
            </>
          ) : null}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default AdminUsers;
